import { useState, useEffect, useRef } from "react"
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock"
import { motion, AnimatePresence, useIsPresent } from "framer-motion"
import { Link, useStaticQuery, graphql } from "gatsby"
import { RiArrowDownSLine } from "react-icons/ri"
import Button from "@/components/Button"
import DropdownNavItem from "@/components/DropdownNavItem"
import UserRecognizer from "@/components/UserRecognizer"
import * as styles from "@/styles/modules/MobileNav.module.scss"

const MobileNav = ({ navItems, activeUser, isOpen }) => {
  return (
    <AnimatePresence>
      {isOpen && <Nav navItems={navItems} activeUser={activeUser} />}
    </AnimatePresence>
  )
}

MobileNav.propTypes = {}

function Nav({ navItems, activeUser }) {
  const data = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          userNavItems {
            emoji
            text
            type
            url
          }
        }
      }
    }
  `)

  const userNavItems =
    data.site?.siteMetadata?.userNavItems?.filter(item => item.text) || []


  const navRef = useRef(null)
  const isPresent = useIsPresent()

  useEffect(() => {
    if (isPresent) {
      disableBodyScroll(navRef.current)
    } else {
      clearAllBodyScrollLocks()
    }
    return () => clearAllBodyScrollLocks()
  }, [isPresent])

  return (
    <motion.div
      className={styles.wrapper}
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ ease: [0.3, 0, 0, 1], duration: 0.4 }}
    >
      <nav className={styles.nav} ref={navRef}>
        {navItems.map(item => {
          return (
            ((item.loggedInOnly && activeUser.ambassadorPermission) ||
              (!item.loggedInOnly && !item.loggedOutOnly) ||
              (item.loggedOutOnly && !activeUser.ambassadorPermission)) && (
              <NavItem
                key={item._key}
                itemText={item.text}
                linkData={item.navItemUrl}
              >
                {item.hasSubnav &&
                  item.subLinks.map(link => {
                    return (
                      <DropdownNavItem
                        key={link._key}
                        itemText={link.text}
                        emoji={link.emoji}
                        title={link.title}
                        url={`${item.navItemUrl.internalLink}/${link.slug.current}`}
                      />
                    )
                  })}
              </NavItem>
            )
          )
        })}
        <NavItem
          itemText="Subscription Preferences"
          linkData={{
            internalLink: "/subscription-preferences",
            title: "Subscription Preferences",
            linkType: "internal",
          }}
        />
        {activeUser.ambassadorPermission ? (
          <>
            {userNavItems.length > 0
              ? userNavItems.map(({ emoji, text, type, url }, idx) => (
                <NavItem
                  key={text + idx}
                  itemText={emoji ? `${emoji} ${text}` : text}
                  linkData={{
                    linkType: type,
                    internalLink: url,
                  }}
                />
              ))
             : null}
            <UserRecognizer
              className={styles.userRecognizer}
              identifier={activeUser.email || activeUser.phone}
            />
          </>
        ) : (
          <div className={styles.bottomCtaWrapper}>
            <Button uiType="secondary" to="/" state={{ showLoginForm: true }}>
              Already Subscribed?
            </Button>
            <Button to="/news-personality-quiz">Subscribe</Button>
          </div>
        )}
      </nav>
    </motion.div>
  )
}

function NavItem({ itemText, linkData = {}, children }) {
  const [isOpen, setOpen] = useState(false)
  const [dropdownHeight, setDropdownHeight] = useState(0)

  const dropdownNav = useRef(null)

  const { internalLink, blank, href, title, linkType } = linkData

  useEffect(() => {
    if (dropdownNav.current) {
      setDropdownHeight(dropdownNav.current.offsetHeight)
    }
  }, [])

  function handleOpenDropdown() {
    setOpen(state => !state)
  }

  return (
    <>
      <div
        className={[styles.navItem, isOpen ? styles.isOpen : ""].join(" ")}
        style={{ height: isOpen ? dropdownHeight + 60 : 60 }}
      >
        <div className={styles.linkWrapper}>
          {linkType === "internal" && internalLink ? (
            <Link
              to={internalLink}
              className={styles.link}
              title={title}
              tabIndex="0"
            >
              {itemText}
            </Link>
          ) : linkType === "external" && href ? (
            // eslint-disable-next-line
            <a
              className={styles.link}
              href={href}
              title={title}
              target={blank ? "_blank" : null}
              rel={blank ? "noreferrer" : null}
            >
              {itemText}
            </a>
          ) : (
            <p className={styles.link}>{itemText}</p>
          )}
          {children && (
            <button className={styles.dropdownBtn} onClick={handleOpenDropdown}>
              <RiArrowDownSLine />
            </button>
          )}
        </div>
        {children && (
          <div className={styles.dropdownNav} ref={dropdownNav}>
            {children}
          </div>
        )}
      </div>
      <hr className={styles.lineRule} />
    </>
  )
}

export default MobileNav
